import React, { useMemo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, TrendingDown, DollarSign, Coins, Weight, Target, RefreshCw } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import { formatCurrency } from '../lib/utils'
import { apiFetch } from '../lib/api'

interface GoldPortfolioSummaryProps {
  portfolioId: number
}

const GoldPortfolioSummary: React.FC<GoldPortfolioSummaryProps> = ({ portfolioId }) => {
  const { portfolioHoldings, getCurrentPrice, exchangeRate, fetchGoldPrice } = usePortfolioStore()
  
  // State for current gold market prices
  const [goldMarketPrices, setGoldMarketPrices] = useState<{
    bid: number
    ask: number
    diff: number
    timestamp: string
  } | null>(null)
  const [loadingPrices, setLoadingPrices] = useState(false)
  
  const goldHoldings = useMemo(() => {
    const holdings = portfolioHoldings[portfolioId] || []
    return holdings.filter(h => h.port_api_type === 'gold')
  }, [portfolioHoldings, portfolioId])

  const goldSummary = useMemo(() => {
    if (goldHoldings.length === 0) {
      return {
        totalWeight: 0,
        totalCost: 0,
        currentValue: 0,
        averagePrice: 0,
        gainLoss: 0,
        gainLossPercent: 0
      }
    }

    let totalWeight = 0
    let totalCost = 0
    let currentValue = 0

    goldHoldings.forEach(holding => {
      const weight = holding.shares // Using shares field to store baht weight
      const purchasePrice = holding.purchase_price
      const currentPrice = getCurrentPrice(holding.symbol, 'THB') // Gold is always in THB
      
      totalWeight += weight
      totalCost += purchasePrice * weight
      currentValue += currentPrice * weight
    })

    const averagePrice = totalWeight > 0 ? totalCost / totalWeight : 0
    const gainLoss = currentValue - totalCost
    const gainLossPercent = totalCost > 0 ? (gainLoss / totalCost) * 100 : 0

    return {
      totalWeight,
      totalCost,
      currentValue,
      averagePrice,
      gainLoss,
      gainLossPercent
    }
  }, [goldHoldings, getCurrentPrice])

  // Function to fetch current gold market prices
  const fetchGoldMarketPrices = async () => {
    setLoadingPrices(true)
    try {
      const response = await apiFetch('/api/prices/gold')
      if (response.ok) {
        const data = await response.json()
        setGoldMarketPrices({
          bid: data.bid,
          ask: data.ask,
          diff: data.diff,
          timestamp: data.timestamp
        })
      }
    } catch (error) {
      console.error('Failed to fetch gold market prices:', error)
    } finally {
      setLoadingPrices(false)
    }
  }

  // Fetch current gold price when component mounts
  useEffect(() => {
    if (goldHoldings.length > 0) {
      fetchGoldPrice().catch(error => {
        console.log('Failed to fetch current gold price:', error)
      })
      fetchGoldMarketPrices()
    }
  }, [goldHoldings.length, fetchGoldPrice])

  const summaryCards = useMemo(() => [
    {
      title: 'Total Weight',
      value: `${goldSummary.totalWeight.toFixed(2)} บาท`,
      subtitle: `${(goldSummary.totalWeight * 15.244).toFixed(2)} กรัม`,
      icon: Weight,
      color: 'yellow'
    },
    {
      title: 'Total Cost',
      value: formatCurrency(goldSummary.totalCost, 'THB'),
      subtitle: `≈ ${formatCurrency(goldSummary.totalCost / exchangeRate, 'USD')}`,
      icon: DollarSign,
      color: 'blue'
    },
    {
      title: 'Current Value',
      value: formatCurrency(goldSummary.currentValue, 'THB'),
      subtitle: `≈ ${formatCurrency(goldSummary.currentValue / exchangeRate, 'USD')}`,
      icon: Coins,
      color: 'orange'
    },
    {
      title: 'Average Price',
      value: `฿${goldSummary.averagePrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      subtitle: 'per baht',
      icon: Target,
      color: 'purple'
    },
    {
      title: 'Profit & Loss',
      value: formatCurrency(goldSummary.gainLoss, 'THB'),
      subtitle: `${goldSummary.gainLoss >= 0 ? '+' : ''}${goldSummary.gainLossPercent.toFixed(2)}%`,
      icon: goldSummary.gainLoss >= 0 ? TrendingUp : TrendingDown,
      color: goldSummary.gainLoss >= 0 ? 'green' : 'red'
    }
  ], [goldSummary, exchangeRate])

  // Stat tiles: neutral by default, tinted only for P&L; gold (warning hue) marks the gold icon
  const getCardColors = (color: string) => {
    const colors = {
      green: 'stat-tile-gain',
      red: 'stat-tile-loss',
    }
    return colors[color as keyof typeof colors] || (color === 'blue' ? 'stat-tile-primary' : 'stat-tile-violet')
  }

  const getIconColor = (color: string) => {
    const colors = {
      yellow: 'text-warning-600 dark:text-warning-400',
      green: 'text-gain',
      red: 'text-loss'
    }
    return colors[color as keyof typeof colors] || 'text-gray-500 dark:text-gray-400'
  }

  if (goldHoldings.length === 0) {
    return (
      <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-xl p-6">
        <div className="text-center">
          <div className="text-4xl mb-3">🥇</div>
          <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            No Gold Holdings
          </h3>
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            Add your first gold purchase to start tracking your precious metals portfolio.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="text-3xl">🥇</div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Gold Portfolio Summary
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Overview of your precious metals investments
            </p>
          </div>
        </div>
        
        <button
          onClick={fetchGoldMarketPrices}
          disabled={loadingPrices}
          className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-200 dark:hover:bg-yellow-900/40 transition-colors disabled:opacity-50"
          title="Refresh gold prices"
        >
          <RefreshCw className={`w-4 h-4 ${loadingPrices ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Current Gold Market Prices */}
      {goldMarketPrices && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
              <Coins className="w-5 h-5" />
              ราคาซื้อ-ขาย ทองแท่ง ปัจจุบัน
            </h3>
            <div className="text-xs text-yellow-600 dark:text-yellow-400">
              ข้อมูลจากสมาคมค้าทองคำไทย • อัพเดทแบบเรียลไทม์
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Buy Price (Bid) */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="text-sm text-gain mb-1">ราคาซื้อ (Bid)</div>
              <div className="text-xl font-bold text-green-700 dark:text-green-300">
                ฿{goldMarketPrices.bid.toLocaleString()}
              </div>
              <div className="text-xs text-green-500 dark:text-green-400">ต่อ 1 บาท (15.244g)</div>
            </div>
            
            {/* Sell Price (Ask) */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="text-sm text-loss mb-1">ราคาขาย (Ask)</div>
              <div className="text-xl font-bold text-red-700 dark:text-red-300">
                ฿{goldMarketPrices.ask.toLocaleString()}
              </div>
              <div className="text-xs text-red-500 dark:text-red-400">ต่อ 1 บาท (15.244g)</div>
            </div>
            
            {/* Spread */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="text-sm text-blue-600 dark:text-blue-400 mb-1">ส่วนต่าง (Spread)</div>
              <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                ฿{(goldMarketPrices.ask - goldMarketPrices.bid).toLocaleString()}
              </div>
              <div className={`text-xs flex items-center gap-1 ${
                goldMarketPrices.diff >= 0 ? 'text-gain' : 'text-loss'
              }`}>
                {goldMarketPrices.diff >= 0 ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                {goldMarketPrices.diff >= 0 ? '+' : ''}{goldMarketPrices.diff}
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-center">
            <div className="text-xs text-yellow-600 dark:text-yellow-400">
              อัพเดทล่าสุด: {new Date(goldMarketPrices.timestamp).toLocaleString('th-TH')}
            </div>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {summaryCards.map((card, index) => {
          const Icon = card.icon
          return (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`${getCardColors(card.color)} rounded-3xl p-4`}
            >
              <div className="flex items-center justify-between mb-3">
                <Icon className={`w-5 h-5 ${getIconColor(card.color)}`} />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {card.title}
                </span>
              </div>
              
              <div className="space-y-1">
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {card.value}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {card.subtitle}
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Gold Holdings List */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Coins className="w-5 h-5 text-yellow-600" />
          Gold Holdings Details
        </h3>
        
        <div className="space-y-3">
          {goldHoldings.map((holding, index) => {
            const currentPrice = getCurrentPrice(holding.symbol, 'THB')
            const totalCost = holding.purchase_price * holding.shares
            const currentValue = currentPrice * holding.shares
            const gainLoss = currentValue - totalCost
            const gainLossPercent = totalCost > 0 ? (gainLoss / totalCost) * 100 : 0
            
            return (
              <motion.div
                key={holding.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🥇</span>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {holding.name}
                    </h4>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {holding.shares} บาท × ฿{holding.purchase_price.toLocaleString()} = ฿{totalCost.toLocaleString()}
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="font-medium text-gray-900 dark:text-white">
                    ฿{currentValue.toLocaleString()}
                  </div>
                  <div className={`text-sm ${gainLoss >= 0 ? 'text-gain' : 'text-loss'}`}>
                    {gainLoss >= 0 ? '+' : ''}฿{gainLoss.toLocaleString()} ({gainLossPercent.toFixed(1)}%)
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default GoldPortfolioSummary